import { type FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AuthRahmen } from './auth-rahmen.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import stile from './passwort-setzen-page.module.css';
import {
  erfuelltRegeln,
  MAX_PASSWORT,
  PASSWORT_REGELN,
  PasswortFehler,
  type PasswortDienst,
} from './passwort-service.js';

type Zustand =
  | { art: 'prueft' }
  | { art: 'ungueltig'; text: string }
  | { art: 'formular'; email: string }
  | { art: 'fertig' };

function textVon(fehler: unknown): string {
  return fehler instanceof PasswortFehler
    ? fehler.text
    : 'Das hat nicht geklappt. Bitte versuchen Sie es erneut.';
}

/**
 * Ziel der Links aus Einladung und "Passwort vergessen" (`/passwort-setzen?oobCode=...`, gebaut
 * in apps/backend/src/passwort-link.ts). Ersetzt Firebases Standard-Handlerseite.
 *
 * Vier Zustaende wie auf jedem datenfuehrenden Bildschirm: Link wird geprueft, Link ungueltig,
 * Formular, fertig. Der Code wird ZUERST geprueft, damit niemand ein Passwort ausdenkt, nur um
 * danach zu erfahren, dass der Link laengst abgelaufen ist.
 */
export function PasswortSetzenPage({ dienst }: { dienst: PasswortDienst }) {
  const [parameter] = useSearchParams();
  const oobCode = parameter.get('oobCode') ?? '';

  const [zustand, setZustand] = useState<Zustand>({ art: 'prueft' });
  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    let aktuell = true;
    if (!oobCode) {
      setZustand({
        art: 'ungueltig',
        text: 'Der Link ist unvollständig. Bitte öffnen Sie ihn direkt aus der E-Mail.',
      });
      return;
    }
    setZustand({ art: 'prueft' });
    dienst
      .pruefeCode(oobCode)
      .then((email) => aktuell && setZustand({ art: 'formular', email }))
      .catch(
        (fehler: unknown) => aktuell && setZustand({ art: 'ungueltig', text: textVon(fehler) }),
      );
    return () => {
      aktuell = false;
    };
  }, [dienst, oobCode]);

  async function abschicken(ereignis: FormEvent) {
    ereignis.preventDefault();
    setMeldung(null);
    if (!erfuelltRegeln(passwort)) {
      setMeldung('Das Passwort erfüllt noch nicht alle Anforderungen.');
      return;
    }
    if (passwort !== wiederholung) {
      setMeldung('Die beiden Eingaben stimmen nicht überein.');
      return;
    }
    setLaeuft(true);
    try {
      await dienst.setze(oobCode, passwort);
      setPasswort('');
      setWiederholung('');
      setZustand({ art: 'fertig' });
    } catch (fehler) {
      setMeldung(textVon(fehler));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <AuthRahmen>
      <h1>Passwort festlegen</h1>

      {zustand.art === 'prueft' && <p role="status">Link wird geprüft …</p>}

      {zustand.art === 'ungueltig' && (
        <>
          <div className="eds-alert eds-alert--error" role="alert">
            <p>{zustand.text}</p>
          </div>
          <Link to="/passwort-vergessen" className="eds-button eds-button--primary">
            Neuen Link anfordern
          </Link>
          <Link to="/anmelden">Zur Anmeldung</Link>
        </>
      )}

      {zustand.art === 'fertig' && (
        <>
          <div className="eds-alert eds-alert--success" role="status">
            <p>
              Ihr Passwort ist gespeichert. Melden Sie sich jetzt an. Beim ersten Mal richten Sie
              danach eine Authenticator-App als zweiten Faktor ein.
            </p>
          </div>
          <Link to="/anmelden" className="eds-button eds-button--primary">
            Zur Anmeldung
          </Link>
        </>
      )}

      {zustand.art === 'formular' && (
        <>
          <p className="eds-text-secondary">
            Für das Konto <strong>{zustand.email}</strong>.
          </p>
          {meldung && (
            <div className="eds-alert eds-alert--error" role="alert">
              <p>{meldung}</p>
            </div>
          )}
          <form onSubmit={abschicken}>
            {/* Fuer Passwortmanager: ordnet das neue Passwort dem richtigen Konto zu. */}
            <input type="email" autoComplete="username" value={zustand.email} readOnly hidden />
            <div className="eds-field">
              <label className="eds-field__label" htmlFor="neues-passwort">
                Neues Passwort
              </label>
              <input
                className="eds-field__input"
                id="neues-passwort"
                type="password"
                autoComplete="new-password"
                aria-describedby="passwort-regeln"
                maxLength={MAX_PASSWORT}
                required
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
              />
            </div>
            <ul id="passwort-regeln" className={stile.regeln} aria-label="Anforderungen">
              {PASSWORT_REGELN.map((regel) => {
                const ok = regel.erfuellt(passwort);
                return (
                  <li key={regel.text} className={ok ? stile.erfuellt : stile.offen}>
                    <span className={stile.regelSymbol}>
                      <LinienSymbol name={ok ? 'check' : 'dot'} groesse={16} />
                    </span>
                    {regel.text}
                    <span className={stile.unsichtbar}>{ok ? ' (erfüllt)' : ' (offen)'}</span>
                  </li>
                );
              })}
            </ul>
            <div className="eds-field">
              <label className="eds-field__label" htmlFor="passwort-wiederholung">
                Passwort wiederholen
              </label>
              <input
                className="eds-field__input"
                id="passwort-wiederholung"
                type="password"
                autoComplete="new-password"
                maxLength={MAX_PASSWORT}
                required
                value={wiederholung}
                onChange={(e) => setWiederholung(e.target.value)}
              />
            </div>
            <button className="eds-button eds-button--primary" type="submit" disabled={laeuft}>
              {laeuft ? 'Wird gespeichert …' : 'Passwort speichern'}
            </button>
          </form>
        </>
      )}
    </AuthRahmen>
  );
}
